function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import Media from '../Media';
import MediaFrame from '../MediaFrame';
import PlayerName from './PlayerName';
import { classNamesFor } from '../utils/classNames';
import findAndDecorateImageProps from '../utils/findAndDecorateImageProps';
var cx;
/**
 * The PlayerCard component displays user related information to be used in a variety of contexts.
 * Often this information is presented in a card form within a list of related players. To allow
 * composition this component is purely presentational in nature. To add interactivity wrap the
 * component in a Button, link (a tag), Checkbox, or RadioGroup / Radio
 *
 * ## Composed of
 * - [PlayerName](./PlayerName/README.md)
 *
 * ## Related Components
 * - [AvatarIcon](../AvatarIcon/README.md)
 * - [AvatarIconGroup](../AvatarIconGroup/README.md)
 * - [MiniProfile](../MiniProfile/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <Player
 *   accountId="1234"
 *   onlineId="mepsn"
 *   image={(
 *    <AvatarIcon
 *      src="https://avatars/profile/1.webp"
 *      alt="mepsn's avatar image"
 *      withAlert
 *    />
 *   )}
 *   trophyLevel={10}
 *   currentActivityPlatform="ps4"
 *   currentActivityTitle="God of War"
 *   isOfficiallyVerified
 *   isPsPlusMember
 * />
 *```
 *
 * As a Button
 * ```jsx
 * <Button
 *    variant="content"
 *    onClick={() => console.log('player clicked')}
 * >
 *   <Player {...props} />
 * </Button>
 *```
 *
 * As a Link
 * ```jsx
 * <a href="/player/1234">
 *   <Player {...props} />
 * </Button>
 *```
 *
 * As a Checkbox or Radio
 * ```jsx
 * <Checkbox
 *   variant="content"
 * >
 *   {(checked) => (
 *     <>
 *       <Player {...props} />
 *       {checked && <Icon type="check" />}
 *     </>
 *   )}
 * </Checkbox>
 *```
 *
 * ## Props
 */

var Player = function Player(props) {
  var qa = props.qa,
      className = props.className,
      name = props.name,
      onlineId = props.onlineId,
      image = props.image,
      trophyLevel = props.trophyLevel,
      currentActivityPlatform = props.currentActivityPlatform,
      currentActivityTitle = props.currentActivityTitle,
      size = props.size,
      trophyLevelLabel = props.trophyLevelLabel;

  var x = props.className,
      playerOnlyProps = _objectWithoutProperties(props, ["className"]);

  var _Player$getClassName = Player.getClassName({
    className: className,
    size: size
  }),
      root = _Player$getClassName.root,
      imageClass = _Player$getClassName.image,
      info = _Player$getClassName.info,
      secondaryInfo = _Player$getClassName.secondaryInfo;

  var fallback = /*#__PURE__*/React.createElement(MediaFrame, {
    className: "psw-bg-3",
    aspectRatio: "1:1"
  }, /*#__PURE__*/React.createElement(Icon, {
    className: "psw-center",
    type: "ps-user"
  }));
  var decoratedImage = findAndDecorateImageProps(image, {
    qa: "".concat(qa, "#avatarIcon"),
    size: size,
    className: cx('psw-fill-x')
  });
  return /*#__PURE__*/React.createElement("article", {
    "data-qa": qa,
    className: root
  }, /*#__PURE__*/React.createElement("div", {
    className: imageClass
  }, decoratedImage || fallback), /*#__PURE__*/React.createElement("div", {
    className: info
  }, /*#__PURE__*/React.createElement(PlayerName, _extends({}, playerOnlyProps, {
    className: cx('psw-fill-x'),
    qa: "".concat(qa, "#playerName1")
  })), name && onlineId && /*#__PURE__*/React.createElement(PlayerName, {
    qa: "".concat(qa, "#playerName2"),
    className: cx(secondaryInfo, 'psw-fill-x'),
    onlineId: onlineId
  }), size === 'large' && trophyLevel && /*#__PURE__*/React.createElement("div", {
    "data-qa": "".concat(qa, "#trophyLevel"),
    className: cx('psw-s-num', 'psw-truncate-text-1', 'psw-fill-x')
  }, /*#__PURE__*/React.createElement(Icon, {
    qa: "".concat(qa, "#trophyLevelIcon"),
    type: "image:trophy-level.png",
    size: "s",
    label: trophyLevelLabel
  }), trophyLevel), currentActivityTitle && /*#__PURE__*/React.createElement("div", {
    "data-qa": "".concat(qa, "#nowPlaying"),
    className: cx('psw-truncate-text-1', 'psw-fill-x', secondaryInfo)
  }, /*#__PURE__*/React.createElement(Icon, {
    qa: "".concat(qa, "#nowPlayingPlatformIcon"),
    className: cx('psw-m-r-xs'),
    type: currentActivityPlatform || 'game',
    size: "s"
  }), currentActivityTitle)));
};

Player.getClassName = function (_ref) {
  var className = _ref.className,
      size = _ref.size;
  return {
    root: cx('psw-player', "psw-player--".concat(size), 'psw-l-line-left', className),
    image: cx('psw-player-image'),
    info: cx('psw-player-info', 'psw-l-stack-left', 'psw-p-l-s', 'psw-l-line-enable-truncation'),
    secondaryInfo: cx('psw-body-2', 'psw-c-secondary')
  };
};

Player.useClassNames = function (classNames) {
  Player.classNames = classNames;
  PlayerName.useClassNames(classNames);
};

Player.defaultProps = {
  qa: '',
  className: '',
  size: 'medium',
  name: '',
  onlineId: '',
  accountId: '',
  isOfficiallyVerified: false,
  isPsPlusMember: false,
  image: null,
  trophyLevel: 1,
  currentActivityTitle: null,
  currentActivityPlatform: null,
  trophyLevelLabel: ''
};
Player.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The size of the card is generally linked to the size of the AvatarIcon used
   */
  size: PropTypes.string,

  /**
   * The players real name (if user is real name friend)
   */
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * The players latest onlineId (always available for display)
   */
  onlineId: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * The unique id associated with the PSN users account
   */
  accountId: PropTypes.string,

  /**
   * Whether the user is a verified user or not. Verified users are users who are
   * - Game developers
   * - Game publishers
   * - Other VIP ;)
   */
  isOfficiallyVerified: PropTypes.bool,

  /**
   * Whether the user is a PS Plus subscriber
   */
  isPsPlusMember: PropTypes.bool,

  /**
   * The players profile image or avatar passed in as an AvatarIcon
   */
  image: PropTypes.node,

  /**
   * The players PSN trophy level (number greater than or equal to 1). Represents
   * there experience with the console
   */
  trophyLevel: PropTypes.number,

  /**
   * "Now Playing" what the player is currently playing
   * The platform being used
   * [TODO: add all platforms]
   */
  currentActivityPlatform: PropTypes.oneOf(['ps4']),

  /**
   * Name of the game being played
   */
  currentActivityTitle: PropTypes.string,

  /**
   * The label for screen readers to announce that the users trophy level
   */
  trophyLevelLabel: PropTypes.string
};
cx = classNamesFor(Player);
export default Player;